import React, { useState } from "react";

import { Box, Button, Stack, Typography } from "@mui/material";
import { unfreezeWeeklyQuiz } from "API/calls";
import BackdropLoading from "components/elements/BackdropLoading";
import { CreditInverted } from "components/icons";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import useConfirm from "hooks/useConfirm";
import useResponsiveFont from "hooks/useResponsiveFont";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { WeeklyQuizStatus } from "types/enums";

interface IProps {
  mutateData: () => void;
  status: WeeklyQuizStatus;
  unfreezeCreditsCosts: { [key: string]: number };
}

const UnfreezeQuizBox: React.FC<IProps> = ({ mutateData, status, unfreezeCreditsCosts }) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const { getResponsiveRem } = useResponsiveFont();
  const { colors } = useMUIThemeModeContext();
  const { refreshData } = useUserDataContext();
  const [isOpenBackdropLoading, setIsOpenBackdropLoading] = useState<boolean>(false);

  const unfreezeCost = unfreezeCreditsCosts[status] ?? 0;

  const unfreezeQuiz = async () => {
    try {
      await confirm({
        title: t("bitcoinerChallenge.weeklyQuiz.unfreeze.confirm.title"),
        description: t("bitcoinerChallenge.weeklyQuiz.unfreeze.confirm.description", { amount: unfreezeCost }),
        dialogProps: { maxWidth: "xs" },
        confirmationText: t("bitcoinerChallenge.weeklyQuiz.unfreeze.confirm.cta"),
      });

      setIsOpenBackdropLoading(true);
      try {
        await unfreezeWeeklyQuiz();
        enqueueSnackbar(t("bitcoinerChallenge.weeklyQuiz.unfreeze.success"), { variant: "success" });
        mutateData();
        refreshData?.();
      } catch (error: any) {
        if (error?.response?.status === 409) {
          enqueueSnackbar(t("bitcoinerChallenge.weeklyQuiz.unfreeze.errorNotEnoughCredits"), { variant: "error" });
        } else {
          enqueueSnackbar(t("bitcoinerChallenge.weeklyQuiz.unfreeze.error"), { variant: "error" });
        }
      }
      setIsOpenBackdropLoading(false);
    } catch (err: any) {
      // silent
    }
  };

  return (
    <>
      <Box p={1.5} mt={3} bgcolor={colors.white} borderRadius={2}>
        <Stack justifyContent={"space-between"} spacing={4} direction={"row"} alignItems={"center"}>
          <Typography
            fontSize={getResponsiveRem(0.85)}
            color={colors.gray500}
            fontFamily={"Nunito"}>
            <Trans
              i18nKey={"bitcoinerChallenge.weeklyQuiz.frozenInfo"}
              values={{ unfreezeCost }}
            />
          </Typography>
          <Button variant="contained" size="small" onClick={unfreezeQuiz} endIcon={<CreditInverted />}>
            {`-${unfreezeCost}`}
          </Button>
        </Stack>
      </Box>
      <BackdropLoading open={isOpenBackdropLoading} />
    </>
  );
};

export default UnfreezeQuizBox;
